import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, Modal, Form, Input, Switch, Space, Tag, Select, App } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDepositOptions, addFiatMethod, updateFiatMethod, deleteFiatMethod } from '../../store/slices/depositSlice';
import Loadingpage from "../Loadingpage/Loadingpage";


const FiatSettings = () => {

    const { modal, message } = App.useApp();
    const dispatch = useDispatch();
    const { fiatMethods, loading } = useSelector((state) => state.deposit);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [page, setPage] = useState({ current: 1, pageSize: 10 });
    const [form] = Form.useForm();
    const methodType = Form.useWatch('type', form);

    const initialValues = useMemo(() => ({
        type: selectedMethod?.type || 'upi',
        is_active: selectedMethod?.is_active || true,
        upi_id: selectedMethod?.details?.upi_id || '',
        min_withdraw: selectedMethod?.min_withdraw || '',
        account_name: selectedMethod?.details?.account_name || '',
        account_number: selectedMethod?.details?.account_number || '',
        ifsc: selectedMethod?.details?.ifsc || '',
        wallet_name: selectedMethod?.details?.wallet_name || '',
        wallet_id: selectedMethod?.details?.wallet_id || '',
    }), [selectedMethod])

    useEffect(() => {
        dispatch(fetchDepositOptions());
    }, [dispatch]);

    useEffect(() => {
        if (isModalOpen) {
            form.setFieldsValue(initialValues);
        }
    }, [isModalOpen, initialValues, form]);

    const handleSubmit = async (values) => {
        try {
            const payload = {
                ...values,
                name: values.account_name || values.upi_id || values.wallet_name,
                type: values.type,
                is_active: values.is_active,
                details: {
                    upi_id: values.upi_id,
                    account_name: values.account_name,
                    account_number: values.account_number,
                    ifsc: values.ifsc,
                    wallet_name: values.wallet_name,
                    wallet_id: values.wallet_id,
                }
            };

            if (selectedMethod) {
                await dispatch(updateFiatMethod({ id: selectedMethod._id, data: payload })).unwrap();
                message.success('Payment method updated successfully');
            } else {
                await dispatch(addFiatMethod(payload)).unwrap();
                message.success('Payment method added successfully');
            }

            setIsModalOpen(false);
            form.resetFields();
            setSelectedMethod(null);
            dispatch(fetchDepositOptions());
        } catch (err) {
            message.error(err.message || 'Failed to save method');
        }
    };

    const handleDelete = (id) => {
        modal.confirm({
            title: 'Are you sure you want to delete this payment method?',
            icon: <ExclamationCircleOutlined />,
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            async onOk() {
                try {
                    await dispatch(deleteFiatMethod(id)).unwrap();
                    message.success('Method deleted successfully');
                    dispatch(fetchDepositOptions());
                } catch (err) {
                    message.error(err.message || 'Failed to delete method');
                }
            },
        });
    };

    const columns = [
        {
            title: 'Method',
            dataIndex: 'type',
            key: 'type',
            render: (text) => <Tag color="#FC4420" style={{ textTransform: 'uppercase' }}>{text || 'FIAT'}</Tag>
        },
        {
            title: 'Details',
            key: 'details',
            render: (_, record) => {
                return (
                    <div style={{ fontSize: '12px', color: '#fff' }}>
                        {record.details?.upi_id && <span>UPI ID: {record.details.upi_id}</span>}
                        {record.details?.account_number && (
                            <div>
                                <div>Name: {record.details.account_name}</div>
                                <div>A/C: {record.details.account_number}</div>
                                <div>IFSC: {record.details.ifsc}</div>
                            </div>
                        )}
                        {record.details?.wallet_id && <span>Wallet: {record.wallet_name} | ID: {record.wallet_id}</span>}
                    </div>
                );
            }
        },
        {
            title: 'Minimum Withdraw',
            dataIndex: 'min_withdraw',
            key: 'min_withdraw',
            render: (min_withdraw) => <span>{min_withdraw}</span>
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (active) => <Switch checked={active} disabled />
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        style={{ color: '#FC4420' }}
                        type="link"
                        onClick={() => {
                            setSelectedMethod(record);
                            setIsModalOpen(true);
                        }}
                    />
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        type="link"
                        onClick={() => handleDelete(record._id)}
                    />
                </Space>
            ),
        },
    ];

    return (
        <div className="p-20">
            <Loadingpage />
            <div className="card-clr" style={{ padding: '20px', borderRadius: '16px', border: '1px solid #FC442080' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ color: '#fff', margin: 0 }}>Fiat Payment Settings</h3>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        setSelectedMethod(null);
                        form.resetFields();
                        setIsModalOpen(true);
                    }}
                    style={{ backgroundColor: '#FC4420', borderColor: '#FC4420' }}
                >
                    Add Method
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={fiatMethods}
                loading={{
                    spinning: loading,
                    indicator: <Loadingpage isLoading={loading} />,
                }}
                pagination={{
                    total: fiatMethods.length,
                    current: page.current,
                    pageSize: page.pageSize,
                    onChange: (p, s) => setPage({ current: p, pageSize: s }),
                    className: "pagination-clr",
                }}
                rowKey="_id"
                className="table-clr"
                scroll={{ x: 'max-content' }}
            />

            <Modal
                title={selectedMethod ? "Edit Payment Method" : "Add Payment Method"}
                open={isModalOpen}
                onCancel={() => {
                    setIsModalOpen(false);
                    setSelectedMethod(null);
                }}
                onOk={() => form.submit()}
                width={500}
                className="custom-modal"
                centered
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={initialValues}>
                    <Form.Item name="type" label="Method Type" rules={[{ required: true }]}>
                        <Select className="selected-cls">
                            <Select.Option value="upi">UPI</Select.Option>
                            <Select.Option value="bank">Bank Transfer</Select.Option>
                            <Select.Option value="wallet">Wallet (Paytm, etc.)</Select.Option>
                        </Select>
                    </Form.Item>

                    {methodType === 'upi' && (
                        <Form.Item name="upi_id" label="UPI ID" rules={[{ required: true }]}>
                            <Input placeholder="e.g. user@upi" className="input-cls" />
                        </Form.Item>
                    )}

                    {methodType === 'bank' && (
                        <>
                            <Form.Item name="account_name" label="Account Holder Name" rules={[{ required: true }]}>
                                <Input placeholder="Name" className="input-cls" />
                            </Form.Item>
                            <Form.Item name="account_number" label="Account Number" rules={[{ required: true }]}>
                                <Input placeholder="Number" className="input-cls" />
                            </Form.Item>
                            <Form.Item name="ifsc" label="IFSC Code" rules={[{ required: true }]}>
                                <Input placeholder="IFSC" className="input-cls" />
                            </Form.Item>
                        </>
                    )}

                    {methodType === 'wallet' && (
                        <>
                            <Form.Item name="wallet_name" label="Wallet Name" rules={[{ required: true }]}>
                                <Input placeholder="e.g. Paytm" className="input-cls" />
                            </Form.Item>
                            <Form.Item name="wallet_id" label="Wallet ID / Mobile" rules={[{ required: true }]}>
                                <Input placeholder="Enter ID" className="input-cls" />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item name="min_withdraw" label="Minimum Withdraw" rules={[{ required: true }]}>
                        <Input placeholder="Enter Minimum Withdraw" className="input-cls" />
                    </Form.Item>

                    <Form.Item name="is_active" label="Enable Method" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
        </div>
    );
};

export default FiatSettings;
